import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function TeamAnnouncement() {
  const { token, isCoach } = useAuth();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [sendToAll, setSendToAll] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const teams = [...new Set(data.filter(a => a.team).map(a => a.team))].sort();
        setAvailableTeams(teams);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const toggleTeam = (team) => {
    if (selectedTeams.includes(team)) {
      setSelectedTeams(selectedTeams.filter(t => t !== team));
    } else {
      setSelectedTeams([...selectedTeams, team]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/teams/announcement`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ 
          subject, 
          message,
          teams: sendToAll ? null : selectedTeams
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResult({
          type: 'success',
          message: `Announcement sent! ${data.stats.sent} emails delivered, ${data.stats.skipped} skipped (user preference).`
        });
        // Clear form on success
        setSubject('');
        setMessage('');
      } else {
        setResult({
          type: 'error',
          message: data.error || 'Failed to send announcement'
        });
      }
    } catch (error) {
      console.error('Error sending announcement:', error);
      setResult({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setSending(false);
    }
  };

  if (!isCoach()) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600">Only coaches and admins can send team announcements.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-theme-primary">Send Team Announcement</h2>
        <p className="text-gray-600 mt-1">
          Send an email to team members. Members can opt out in their email preferences.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Team Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Send To
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={sendToAll}
                onChange={() => setSendToAll(true)}
                className="w-4 h-4 text-theme-primary"
              />
              <span>All club members</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!sendToAll}
                onChange={() => setSendToAll(false)}
                className="w-4 h-4 text-theme-primary"
              />
              <span>Specific teams only</span>
            </label>
          </div>
          
          {!sendToAll && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg border">
              <p className="text-sm text-gray-600 mb-2">Select teams to notify:</p>
              {availableTeams.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {availableTeams.map((team) => (
                    <button
                      key={team}
                      type="button"
                      onClick={() => toggleTeam(team)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                        selectedTeams.includes(team)
                          ? 'bg-theme-primary text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {team}
                      {selectedTeams.includes(team) && ' ✓'}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No teams found. Create teams in Club Management first.</p>
              )}
              {selectedTeams.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {selectedTeams.join(', ')}
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g., Practice Schedule Change"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your announcement message..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus-theme-primary min-h-[200px]"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Basic HTML formatting is supported (bold, links, etc.)
          </p>
        </div>

        {/* Preview */}
        {(subject || message) && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <p className="text-xs text-gray-500 mb-2">Preview:</p>
            <h3 className="font-bold text-theme-primary">{subject || 'Subject'}</h3>
            <div className="mt-2 text-gray-700 whitespace-pre-wrap">
              {message || 'Your message will appear here...'}
            </div>
          </div>
        )}

        {/* Result message */}
        {result && (
          <div className={`p-4 rounded-lg ${
            result.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {result.message}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              setSubject('');
              setMessage('');
              setResult(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={sending}
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={sending || !subject || !message || (!sendToAll && selectedTeams.length === 0)}
            className="px-6 py-2 rounded-lg font-medium text-white btn-theme-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : sendToAll ? 'Send to All' : `Send to ${selectedTeams.length} Team${selectedTeams.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </form>
    </div>
  );
}

